import React, { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import _ from 'underscore'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'
import { SortableContainer, SortableElement, SortableHandle, SortableElementProps, SortableContainerProps } from 'react-sortable-hoc'

import StatusCell from 'pages/shared/StatusCell'

import Table from 'components/Table'

import { useStoreContext } from 'hooks/StoreContext'
import useDeviceMetrics from 'hooks/useDeviceMetrics'
import useGroups from 'hooks/useGroups'

import { Circle, LoadingSinglePanePage, screenSize } from 'styles/shared'
import { Trend } from 'types/DeviceMetric'

type ItemProps = {
  trend: Trend
}

type ListProps = {
  items: Trend[],
}

type SortableTrendRowsProps = {
    items: Trend[],
    onSortEndCallback: (oldIndex, newIndex)=> void,
    isEditing: boolean
  }

const DragHandle = SortableHandle(() => <S.DragHandle />)

const SortableTrendRows = ({ items, isEditing, onSortEndCallback }: SortableTrendRowsProps) => {
  useDeviceMetrics()

  const { groups, areGroupsLoaded } = useGroups()

  const { deviceMetrics, areDeviceMetricsLoaded } = useStoreContext()

  const navigate = useNavigate()
  const trendList = items.sort((a, b) => a.sortPosition - b.sortPosition)

  if (!areGroupsLoaded || !areDeviceMetricsLoaded) {
    return <LoadingSinglePanePage />
  }

  const combinedList = (metricList, groupList) => {
    return [
      ...(metricList || []).map(list => {
        const metric = _.find(deviceMetrics, (metric) => metric.id === list.metricId)

        return {
          id: list.metricId,
          name: metric?.name,
          sortPosition: list.sortPosition,
          color: metric?.color,
          type: 'metric'
        }
      }),
      ...(groupList || []).map(list => ({
        id: list.groupId,
        name: groups.find((group) => group.groupId === list.groupId)?.name,
        sortPosition: list.sortPosition,
        color: undefined,
        type: 'group'
      }))
    ]
  }

  const Item = ({ trend }: ItemProps) => {
    return (
      <S.Row key={trend.id}>
        <S.Cell>
          { isEditing && <DragHandle /> }
          <S.Name>{trend.name}</S.Name>
        </S.Cell>
        <S.Cell>
          <S.ListContainer>
            {_.sortBy(combinedList(trend.metricList, trend.groupList), ['sortPosition']).map((item) => (
              <li key={`${item.type}-${item.id}`}>
                <Circle color={item.color} />
                <span>{item.name}</span>
              </li>
            ))}
          </S.ListContainer>
        </S.Cell>
        <div>
          <StatusCell isEnabled={trend.draft} />
          <EditIcon
            fontSize="small"
            color="primary"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/admin/trends/${trend.id}/edit`)}
            aria-label={`Edit Trend metric with title: ${trend.name}`}
          >
            Edit
          </EditIcon>
        </div>
      </S.Row>
    )
  }

  const List = ({ items }: ListProps) => {
    return (
      <div>
        {items.map((trend, index) => (
          <SortableItem
          key={`item-${trend}`}
          index={index}
          trend={trend}
          />
        ))}
      </div>
    )
  }

  const SortableItem = memo(SortableElement(Item)) as React.MemoExoticComponent<React.ComponentClass<unknown & SortableElementProps & ItemProps>>
  const SortableList = memo(SortableContainer(List)) as React.MemoExoticComponent<React.ComponentClass<unknown & SortableContainerProps & ListProps>>

  return (
    <SortableList items={trendList} onSortEnd={onSortEndCallback} useDragHandle />
  )
}

export default SortableTrendRows

const S = {
  Cell: styled(Table.Cell)`
    padding-left: 10px;
    align-items: center;
  `,
  Name: styled.p`
    overflow-wrap: break-word;
  `,
  Row: styled(Table.Row)`
    display: grid;
    grid-template-columns: 30% 40% 30%;
    align-items: center;
    min-height: 50px;
    padding-top: 10px;
    padding-bottom: 10px;
  `,
  ListContainer: styled.div`
    display: flex;
    flex-direction: column;
    list-style-type: none;
  `,
  StatusCell: styled(Table.Cell)`
    flex: 1;
    flex-direction: column;

    @media ${screenSize.small} {
      display: inline-block;
      width: 50%;
      padding-left: 5px;
    }
  `,
  DragHandle: styled(DragHandleIcon)`
    cursor: grab;
    margin-right: 10px;
  `,
  ButtonWithIcon: styled.span`
    display: flex;
    align-items: center;
    color: white;
    gap: 5px;
    font-weight: 500;
  `
}
